import classNames from "classnames";
import type React from "react";
import { useState } from "react";
import HandleClicksOutside from "../../HandleClicksOutside";
import { Flex } from "../Flex";
import Icon, { type MdIconName } from "../Icon";
import "./MenuButton.css";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import Label from "../Label";

type MenuButtonOption = {
    label: string;
    subLabel?: string;
    onClick: () => void | Promise<void>;
    icon: MdIconName;
};

export type MenuButtonOptions = MenuButtonOption[];

export type MenuButtonProps = {
    className?: string;
    button: React.ReactNode;
    placement?: "left" | "right";
    options: MenuButtonOptions;
};

function MenuButtonItem(props: {
    option: MenuButtonOption;
    setActive: (active: boolean) => void;
}) {
    const { option, setActive } = props;
    const OptionIcon = Icon[option.icon].Medium;
    const [isLoading, setIsLoading] = useState<boolean>(false);

    return (
        <Flex.Row
            key={option.label}
            className="menu-button__options__option"
            gap="medium"
            alignItems={option.subLabel ? "start" : "center"}
            onClick={async (e) => {
                e.stopPropagation();
                setIsLoading(true);
                await option.onClick();
                setIsLoading(false);
                setActive(false);
            }}
        >
            {option.icon && (
                <div className="menu-button__options__option-icon-container">
                    {isLoading ? <LoadingSpinner /> : <OptionIcon />}
                </div>
            )}
            <Flex.Column gap="small">
                <Label.Medium.Light>{option.label}</Label.Medium.Light>

                {option.subLabel && (
                    <Label.Small.Light.N50>{option.subLabel}</Label.Small.Light.N50>
                )}
            </Flex.Column>
        </Flex.Row>
    );
}

export function MenuButton(props: MenuButtonProps) {
    const { options, className, button, placement = "left" } = props;
    const [active, setActive] = useState<boolean>(false);

    return (
        <div className="menu-button__container">
            <Flex.Row
                className={classNames("menu-button", className, {
                    "menu-button--active": active,
                    "menu-button--inactive": !active,
                })}
                gap="medium"
                onClick={(e) => {
                    e.stopPropagation();
                    setActive(!active);
                }}
            >
                {button}
            </Flex.Row>

            {active && (
                <HandleClicksOutside
                    onClickOutside={() => {
                        setActive(false);
                    }}
                    className={classNames("menu-button__options", {
                        "menu-button__options--right": placement === "right",
                    })}
                >
                    {options.map((option) => (
                        <MenuButtonItem
                            key={option.label}
                            option={option}
                            setActive={setActive}
                        />
                    ))}
                </HandleClicksOutside>
            )}
        </div>
    );
}
